import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { listAgent, updateAgentNotes, resetAgentPassword, changeAgentStatus, changeAgentEmailNotify, type UserInfo } from '@/api/agent';

// —— 状态显示逻辑：state=0 启用中 / state=2 已禁用 / expirydate 过期 → state=3 已过期 ——
type DisplayState = 0 | 2 | 3;

function getDisplayState(user: UserInfo): DisplayState {
  if (user.state === 2) return 2;
  if (user.state === 3) return 3;
  // state=0 时检查有效期是否过期
  if (user.expirydate && user.expirydate < Date.now()) return 3;
  return 0;
}

const statusColor: Record<number, string> = {
  0: 'bg-green-50 text-green-700 border-green-200',
  2: 'bg-gray-100 text-gray-500 border-gray-200',
  3: 'bg-red-50 text-red-600 border-red-200',
};

const statusLabel: Record<number, string> = {
  0: '启用中',
  2: '已禁用',
  3: '已过期',
};

function formatDate(timestamp?: number): string {
  if (!timestamp) return '—';
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

type ModalType = 'reset_password' | 'disable' | 'enable' | null;

export default function AgentsPage() {
  const navigate = useNavigate();

  // —— 数据 ——
  const [agents, setAgents] = useState<UserInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // —— 分页 ——
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // —— 筛选 ——
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // —— Toast ——
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // —— 弹窗 ——
  const [modal, setModal] = useState<{ type: ModalType; agent: UserInfo | null }>({ type: null, agent: null });
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPw, setShowResetPw] = useState(false);

  // —— 行内备注编辑 ——
  const [inlineNoteAgentId, setInlineNoteAgentId] = useState<string | null>(null);
  const [inlineNoteValue, setInlineNoteValue] = useState('');
  const [noteEditorPos, setNoteEditorPos] = useState<{ top: number; left: number } | null>(null);
  const [noteSaving, setNoteSaving] = useState(false);
  const inlineNoteRef = useRef<HTMLDivElement | null>(null);

  // —— 邮件通知开关 ——
  const [emailNotifyLoadingId, setEmailNotifyLoadingId] = useState<string | null>(null);

  const closeNoteEditor = () => {
    setInlineNoteAgentId(null);
    setInlineNoteValue('');
    setNoteEditorPos(null);
  };

  // —— 导出菜单 ——
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exportToast, setExportToast] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // —— 请求数据 ——
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const result = await listAgent({
          pageNumber,
          pageSize,
          keyword: debouncedSearch || undefined,
          state: filterStatus !== 'all' ? Number(filterStatus) : undefined,
        });
        if (!cancelled) {
          setAgents(result.list);
          setTotal(result.total);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [pageNumber, pageSize, debouncedSearch, filterStatus]);

  // 导出菜单点击外部关闭
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // 清理搜索定时器
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  // 点击备注编辑区域外部时关闭
  useEffect(() => {
    if (!inlineNoteAgentId) return;
    const handleClick = (e: MouseEvent) => {
      if (inlineNoteRef.current && !inlineNoteRef.current.contains(e.target as Node)) {
        closeNoteEditor();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [inlineNoteAgentId]);

  // —— 搜索防抖 ——
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPageNumber(1);
    }, 400);
  };

  // 筛选变更
  const handleStatusFilterChange = (value: string) => {
    setFilterStatus(value);
    setPageNumber(1);
  };

  // —— 分页 ——
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startIndex = (pageNumber - 1) * pageSize + 1;
  const endIndex = Math.min(pageNumber * pageSize, total);

  const goPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setPageNumber(page);
  };

  // 生成页码按钮
  const pageNumbers: number[] = [];
  const maxVisible = 5;
  let pStart = Math.max(1, pageNumber - Math.floor(maxVisible / 2));
  let pEnd = Math.min(totalPages, pStart + maxVisible - 1);
  if (pEnd - pStart + 1 < maxVisible) {
    pStart = Math.max(1, pEnd - maxVisible + 1);
  }
  for (let i = pStart; i <= pEnd; i++) pageNumbers.push(i);

  // —— 弹窗操作 ——
  const closeModal = () => {
    setModal({ type: null, agent: null });
    setShowResetPw(false);
  };

  const handleDisable = async () => {
    if (!modal.agent) return;
    try {
      await changeAgentStatus(modal.agent.id, 2);
    } catch (e: unknown) {
      showToast((e as Error).message || '禁用失败', 'error');
      return;
    }
    showToast(`已禁用 ${modal.agent.name}`);
    setAgents((prev) => prev.map((a) => (a.id === modal.agent!.id ? { ...a, state: 2 } : a)));
    closeModal();
  };

  const handleEnable = async () => {
    if (!modal.agent) return;
    try {
      await changeAgentStatus(modal.agent.id, 0);
    } catch (e: unknown) {
      showToast((e as Error).message || '启用失败', 'error');
      return;
    }
    showToast(`已启用 ${modal.agent.name}`);
    setAgents((prev) => prev.map((a) => (a.id === modal.agent!.id ? { ...a, state: 0 } : a)));
    closeModal();
  };

  const handleResetPassword = async () => {
    if (!modal.agent || !resetPassword.trim()) return;
    try {
      await resetAgentPassword(modal.agent.id, resetPassword);
    } catch (e: unknown) {
      showToast((e as Error).message || '重置密码失败', 'error');
      return;
    }
    showToast(`已成功重置 ${modal.agent.name} 的密码`);
    closeModal();
    setResetPassword('');
  };

  // 保存行内备注
  const handleSaveInlineNote = async (agentId: string) => {
    if (noteSaving) return;
    setNoteSaving(true);
    try {
      await updateAgentNotes(agentId, inlineNoteValue);
      setAgents((prev) => prev.map((a) => (a.id === agentId ? { ...a, notes: inlineNoteValue } : a)));
      closeNoteEditor();
    } catch {
      // 保存失败，保留编辑状态让用户重试
    } finally {
      setNoteSaving(false);
    }
  };

  // —— 导出 CSV ——
  const doExportCSV = (agentList: UserInfo[]) => {
    const headers = ['代理商', '邮箱', '公司', '地区', '状态', '有效期至', '浏览量', '下载量', '备注'];
    const rows = agentList.map((a) => {
      const ds = getDisplayState(a);
      return [
        a.name,
        a.email ?? '',
        a.company ?? '',
        a.region ?? '',
        statusLabel[ds],
        formatDate(a.expirydate),
        String(a.totalviews ?? 0),
        String(a.totaldownloads ?? 0),
        (a.notes ?? '').replace(/\n/g, ' '),
      ];
    });
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csvContent = [headers, ...rows].map((r) => r.map(escape).join(',')).join('\n');
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `agents_export_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportFiltered = () => {
    doExportCSV(agents);
    setExportMenuOpen(false);
    setExportToast(true);
    setTimeout(() => setExportToast(false), 3000);
  };

  const exportSelected = () => {
    const selected = agents.filter((a) => selectedIds.has(a.id));
    if (selected.length > 0) {
      doExportCSV(selected);
    }
    setExportMenuOpen(false);
    setExportToast(true);
    setTimeout(() => setExportToast(false), 3000);
  };

  // —— 批量选择 ——
  const toggleSelectAll = () => {
    if (selectedIds.size === agents.length && agents.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(agents.map((a) => a.id)));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // —— 切换邮件通知 ——
  const handleToggleEmailNotify = async (agent: UserInfo) => {
    if (emailNotifyLoadingId === agent.id) return;
    const nextValue = !(agent.receiveEmail ?? true);
    setEmailNotifyLoadingId(agent.id);
    try {
      await changeAgentEmailNotify(agent.id, nextValue);
      setAgents((prev) =>
        prev.map((a) => (a.id === agent.id ? { ...a, receiveEmail: nextValue } : a))
      );
      showToast(nextValue ? '已开启邮件通知' : '已关闭邮件通知', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : '设置失败', 'error');
    } finally {
      setEmailNotifyLoadingId(null);
    }
  };

  return (
    <AdminLayout title="代理商管理" subtitle={`共 ${total} 个账号`}>
      {/* —— 工具栏 —— */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        {/* 搜索 */}
        <div className="relative flex-1 max-w-sm">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-sm"></i>
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="搜索代理商名称、公司、邮箱..."
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none bg-white"
            onFocus={(e) => { e.target.style.borderColor = '#0069BD'; }}
            onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
          />
        </div>

        {/* 状态筛选 */}
        <div className="relative">
          <select
            value={filterStatus}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="appearance-none pl-3 pr-10 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none bg-white cursor-pointer"
          >
            <option value="all">全部状态</option>
            <option value="0">启用中</option>
            <option value="2">已禁用</option>
            <option value="3">已过期</option>
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-arrow-down-s-line text-base"></i>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {/* 导出下拉菜单 */}
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setExportMenuOpen((v) => !v)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap bg-white"
            >
              <i className="ri-download-2-line text-sm"></i>
              导出
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {exportMenuOpen && (
              <div
                className="absolute right-0 top-full mt-1.5 w-52 bg-white border border-gray-100 rounded-xl overflow-hidden z-[50]"
                style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }}
              >
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">导出范围</p>
                </div>
                {[
                  { label: '导出当前筛选结果', desc: `共 ${agents.length} 条`, action: exportFiltered },
                  { label: '导出已选中', desc: `已选 ${selectedIds.size} 条`, action: exportSelected },
                ].map((opt) => (
                  <button
                    key={opt.label}
                    onClick={opt.action}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer text-left"
                  >
                    <span className="font-medium">{opt.label}</span>
                    <span className="text-xs text-gray-400 ml-2 shrink-0">{opt.desc}</span>
                  </button>
                ))}
                <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/60">
                  <p className="text-xs text-gray-400 leading-relaxed">
                    <i className="ri-information-line mr-1"></i>
                    导出 UTF-8 CSV 格式
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* 新建 */}
          <button
            onClick={() => navigate('/admin/agents/create')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors cursor-pointer whitespace-nowrap"
            style={{ background: '#0069BD' }}
          >
            <i className="ri-user-add-line text-sm"></i>
            新建代理商
          </button>
        </div>
      </div>

      {/* —— 表格 —— */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto min-w-0">
          <table className="w-full" style={{ minWidth: '1150px' }}>
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap sticky left-0 z-10 bg-gray-50/50" style={{ width: '260px', minWidth: '260px' }}>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={agents.length > 0 && selectedIds.size === agents.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-[#0069BD] shrink-0"
                    />
                    代理商
                  </div>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  公司
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  地区
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  状态
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  有效期至
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  浏览量
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  下载量
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  备注
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap sticky right-0 z-10 bg-gray-50/50">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-16 text-center text-gray-400">
                    <i className="ri-loader-4-line text-2xl mb-2 block animate-spin"></i>
                    <p className="text-sm">加载中...</p>
                  </td>
                </tr>
              ) : agents.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-16 text-center text-gray-400">
                    <i className="ri-user-search-line text-3xl mb-2 block"></i>
                    <p className="text-sm">未找到代理商</p>
                  </td>
                </tr>
              ) : (
                agents.map((agent) => {
                  const ds = getDisplayState(agent);
                  return (
                    <tr key={agent.id} className="group hover:bg-gray-50/50 transition-colors">
                      {/* 代理商 */}
                      <td className="px-4 py-3 sticky left-0 z-10 bg-white group-hover:bg-gray-50/50" style={{ width: '260px', minWidth: '260px' }}>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(agent.id)}
                            onChange={() => toggleSelectOne(agent.id)}
                            className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-[#0069BD] shrink-0"
                          />
                          <button
                            onClick={() => navigate(`/admin/agents/${agent.id}/activity`)}
                            className="flex items-center gap-2.5 cursor-pointer hover:opacity-80 transition-opacity text-left"
                            title="查看活动记录"
                          >
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: '#0069BD' }}>
                              {(agent.name || '?')[0]}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-800 whitespace-nowrap group-hover:text-[#0069BD]">{agent.name}</p>
                              <p className="text-xs text-gray-400 whitespace-nowrap">{agent.email ?? agent.loginname}</p>
                            </div>
                          </button>
                        </div>
                      </td>
                      {/* 公司 */}
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{agent.company || '—'}</td>
                      {/* 地区 */}
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{agent.region || '—'}</td>
                      {/* 状态 */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${statusColor[ds]}`}>
                          {statusLabel[ds]}
                        </span>
                      </td>
                      {/* 有效期至 */}
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(agent.expirydate)}</td>
                      {/* 浏览量 */}
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/admin/agents/${agent.id}/activity?tab=files`)}
                          className="text-sm font-semibold text-[#0069BD] hover:underline cursor-pointer"
                          title="查看活动记录"
                        >
                          {(agent.totalviews ?? 0).toLocaleString()}
                        </button>
                      </td>
                      {/* 下载量 */}
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/admin/agents/${agent.id}/activity?tab=files`)}
                          className="text-sm font-semibold text-[#0069BD] hover:underline cursor-pointer"
                          title="查看活动记录"
                        >
                          {(agent.totaldownloads ?? 0).toLocaleString()}
                        </button>
                      </td>
                      {/* 备注 */}
                      <td className="px-4 py-3">
                        {inlineNoteAgentId === agent.id && noteEditorPos && createPortal(
                          <div ref={inlineNoteRef} className="fixed z-[9999] w-80 bg-white border border-gray-200 rounded-xl p-3" style={{ top: noteEditorPos.top, left: noteEditorPos.left, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
                            <textarea
                              value={inlineNoteValue}
                              onChange={(e) => setInlineNoteValue(e.target.value)}
                              rows={3}
                              maxLength={500}
                              autoFocus
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none resize-none"
                              onFocus={(e) => { e.target.style.borderColor = '#0069BD'; }}
                              onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
                              placeholder="填写备注..."
                            />
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-gray-400">{inlineNoteValue.length}/500</span>
                              <div className="flex gap-2">
                                <button
                                  onClick={closeNoteEditor}
                                  className="px-3 py-1.5 text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                                >
                                  取消
                                </button>
                                <button
                                  onClick={() => handleSaveInlineNote(agent.id)}
                                  disabled={noteSaving}
                                  className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50"
                                  style={{ background: '#0069BD' }}
                                >
                                  {noteSaving ? '保存中...' : '保存'}
                                </button>
                              </div>
                            </div>
                          </div>,
                          document.body
                        )}
                        {agent.notes ? (
                          <div className="relative group/notecell">
                            <button
                              onClick={(e) => {
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                setNoteEditorPos({ top: rect.bottom + 8, left: rect.left });
                                setInlineNoteAgentId(agent.id);
                                setInlineNoteValue(agent.notes ?? '');
                              }}
                              className="flex items-center gap-1.5 cursor-pointer"
                              title={agent.notes}
                            >
                              <div className="w-6 h-6 flex items-center justify-center rounded-md text-gray-500 bg-gray-100">
                                <i className="ri-sticky-note-line text-xs"></i>
                              </div>
                              <span className="text-xs text-gray-400 max-w-[100px] truncate hidden lg:block">{agent.notes}</span>
                            </button>
                            <div className="pointer-events-none absolute bottom-full left-0 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 leading-relaxed opacity-0 group-hover/notecell:opacity-100 transition-opacity z-50 whitespace-pre-wrap">
                              {agent.notes}
                              <div className="absolute top-full left-4 w-0 h-0" style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #111827' }}></div>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => {
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              setNoteEditorPos({ top: rect.bottom + 8, left: rect.left });
                              setInlineNoteAgentId(agent.id);
                              setInlineNoteValue('');
                            }}
                            className="flex items-center gap-1 text-xs text-gray-300 hover:text-gray-500 transition-colors cursor-pointer whitespace-nowrap group/addnote"
                            title="添加备注"
                          >
                            <div className="w-6 h-6 flex items-center justify-center rounded-md border border-dashed border-gray-200 group-hover/addnote:border-gray-400">
                              <i className="ri-add-line text-xs"></i>
                            </div>
                          </button>
                        )}
                      </td>
                      {/* 操作 */}
                      <td className="px-4 py-3 sticky right-0 z-10 bg-white group-hover:bg-gray-50/50">
                        <div className="flex items-center gap-1">
                          {/* 活动记录 */}
                          <div className="relative group/tip">
                            <button
                              onClick={() => navigate(`/admin/agents/${agent.id}/activity`)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-[#0069BD] hover:bg-blue-50 transition-colors cursor-pointer"
                            >
                              <i className="ri-bar-chart-line text-sm"></i>
                            </button>
                            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
                              活动记录
                            </span>
                          </div>
                          {/* 重置密码 */}
                          <div className="relative group/tip">
                            <button
                              onClick={() => { setResetPassword(''); setShowResetPw(false); setModal({ type: 'reset_password', agent }); }}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-[#0069BD] hover:bg-blue-50 transition-colors cursor-pointer"
                            >
                              <i className="ri-lock-password-line text-sm"></i>
                            </button>
                            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
                              重置密码
                            </span>
                          </div>
                          {/* 禁用（仅启用中可见） */}
                          {ds === 0 && (
                            <div className="relative group/tip">
                              <button
                                onClick={() => setModal({ type: 'disable', agent })}
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                              >
                                <i className="ri-forbid-line text-sm"></i>
                              </button>
                              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
                                禁用账号
                              </span>
                            </div>
                          )}
                          {/* 启用（仅已禁用可见） */}
                          {ds === 2 && (
                            <div className="relative group/tip">
                              <button
                                onClick={() => setModal({ type: 'enable', agent })}
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors cursor-pointer"
                              >
                                <i className="ri-checkbox-circle-line text-sm"></i>
                              </button>
                              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
                                启用账号
                              </span>
                            </div>
                          )}
                          {/* 启用按钮（已过期不可点击） */}
                          {ds === 3 && (
                            <div className="relative group/tip">
                              <button
                                disabled
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 cursor-not-allowed"
                              >
                                <i className="ri-checkbox-circle-line text-sm"></i>
                              </button>
                              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
                                账号已过期
                              </span>
                            </div>
                          )}
                          {/* 编辑 */}
                          <div className="relative group/tip">
                            <button
                              onClick={() => navigate(`/admin/agents/${agent.id}/edit`)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-[#0069BD] hover:bg-blue-50 transition-colors cursor-pointer"
                            >
                              <i className="ri-pencil-line text-sm"></i>
                            </button>
                            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
                              编辑
                            </span>
                          </div>
                          {/* 邮件通知开关 */}
                          <div className="relative group/tip ml-1">
                            <button
                              type="button"
                              onClick={() => handleToggleEmailNotify(agent)}
                              disabled={emailNotifyLoadingId === agent.id}
                              className={`w-9 h-5 rounded-full transition-colors cursor-pointer relative disabled:opacity-50 disabled:cursor-not-allowed ${
                                (agent.receiveEmail ?? true) ? '' : 'bg-gray-300'
                              }`}
                              style={(agent.receiveEmail ?? true) ? { background: '#0069BD' } : {}}
                              aria-label={(agent.receiveEmail ?? true) ? '关闭邮件通知' : '开启邮件通知'}
                            >
                              <span
                                className="absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all duration-200"
                                style={{
                                  left: (agent.receiveEmail ?? true) ? '18px' : '2px',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                }}
                              />
                              {emailNotifyLoadingId === agent.id && (
                                <span className="absolute inset-0 flex items-center justify-center">
                                  <i className="ri-loader-4-line text-[10px] text-white animate-spin"></i>
                                </span>
                              )}
                            </button>
                            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
                              {(agent.receiveEmail ?? true) ? '邮件通知已开启' : '邮件通知已关闭'}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* —— 分页 —— */}
        {total > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/30">
            <span className="text-xs text-gray-500">
              第 {startIndex}-{endIndex} 条，共 {total} 条
            </span>
            <div className="flex items-center gap-2">
              {/* 每页条数 */}
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPageNumber(1); }}
                className="appearance-none pl-2 pr-7 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 focus:outline-none bg-white cursor-pointer"
              >
                {[10, 20, 50, 100].map((s) => (
                  <option key={s} value={s}>{s} 条/页</option>
                ))}
              </select>

              {/* 页码按钮 */}
              <div className="flex items-center gap-0.5">
                <button
                  onClick={() => goPage(pageNumber - 1)}
                  disabled={pageNumber <= 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>

                {pStart > 1 && (
                  <>
                    <button onClick={() => goPage(1)} className="w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer">1</button>
                    {pStart > 2 && <span className="w-8 h-8 flex items-center justify-center text-xs text-gray-400">...</span>}
                  </>
                )}

                {pageNumbers.map((p) => (
                  <button
                    key={p}
                    onClick={() => goPage(p)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-colors cursor-pointer"
                    style={p === pageNumber ? { background: '#0069BD', color: '#fff' } : { color: '#4b5563' }}
                  >
                    {p}
                  </button>
                ))}

                {pEnd < totalPages && (
                  <>
                    {pEnd < totalPages - 1 && <span className="w-8 h-8 flex items-center justify-center text-xs text-gray-400">...</span>}
                    <button onClick={() => goPage(totalPages)} className="w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer">{totalPages}</button>
                  </>
                )}

                <button
                  onClick={() => goPage(pageNumber + 1)}
                  disabled={pageNumber >= totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* —— 重置密码弹窗 —— */}
      {modal.type === 'reset_password' && modal.agent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900">重置密码</h3>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">为 <strong className="text-gray-700">{modal.agent.name}</strong> 设置新密码</p>
            <div className="relative mb-2">
              <input
                type={showResetPw ? 'text' : 'password'}
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="w-full pr-20 px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-mono text-gray-800 focus:outline-none"
                onFocus={(e) => { e.target.style.borderColor = '#0069BD'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <button type="button" onClick={() => setShowResetPw(!showResetPw)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className={showResetPw ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'}></i>
                </button>
                <button type="button" onClick={() => setResetPassword(generatePassword())} className="text-xs font-semibold px-2 py-1 rounded cursor-pointer whitespace-nowrap" style={{ color: '#0069BD' }}>随机生成</button>
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-5">请通过安全的线下渠道将新密码告知用户。</p>
            <div className="flex gap-3">
              <button onClick={closeModal} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleResetPassword} disabled={!resetPassword.trim()} className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed" style={{ background: '#0069BD' }}>确认重置</button>
            </div>
          </div>
        </div>
      )}

      {/* —— 禁用确认弹窗 —— */}
      {modal.type === 'disable' && modal.agent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-forbid-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-base font-bold text-gray-900 text-center mb-1">禁用账号</h3>
            <p className="text-sm text-gray-500 text-center mb-5">
              确认禁用 <strong>{modal.agent.name}</strong>？其访问权限将立即暂停，可随时重新启用。
            </p>
            <div className="flex gap-3">
              <button onClick={closeModal} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleDisable} className="flex-1 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap">确认禁用</button>
            </div>
          </div>
        </div>
      )}

      {/* —— 启用确认弹窗 —— */}
      {modal.type === 'enable' && modal.agent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-checkbox-circle-line text-2xl text-green-500"></i>
            </div>
            <h3 className="text-base font-bold text-gray-900 text-center mb-1">启用账号</h3>
            <p className="text-sm text-gray-500 text-center mb-5">
              确认启用 <strong>{modal.agent.name}</strong>？其访问权限将立即恢复。
            </p>
            <div className="flex gap-3">
              <button onClick={closeModal} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleEnable} className="flex-1 py-2.5 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600 cursor-pointer whitespace-nowrap">确认启用</button>
            </div>
          </div>
        </div>
      )}
      {/* —— 导出 Toast —— */}
      {exportToast && (
        <div
          className="fixed top-5 right-5 z-[99999] flex items-center gap-3 px-5 py-3.5 rounded-xl text-sm font-semibold text-white bg-green-500"
          style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
        >
          <i className="ri-checkbox-circle-line"></i>
          CSV 文件已导出，请查看下载文件夹
        </div>
      )}

      {/* —— 操作提示 —— */}
      {toast && (
        <div
          className={`fixed top-6 left-1/2 -translate-x-1/2 z-[99999] flex items-center gap-2 px-4 py-3 bg-white border rounded-xl text-sm font-semibold ${toast.type === 'error' ? 'border-red-200 text-red-700' : 'border-green-200 text-green-700'}`}
          style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
        >
          <i className={`${toast.type === 'error' ? 'ri-error-warning-fill text-red-500' : 'ri-checkbox-circle-fill text-green-500'} text-base`}></i>
          {toast.message}
        </div>
      )}
    </AdminLayout>
  );
}
